import * as React from 'react'
import { Typography } from '@lacedltd/laced-components'
import {
  SizeConversionIdsAndPrices,
  SizeConversion,
  Conversion
} from 'types/sizeConversion'
import { useTranslation } from 'hooks'
import { withTranslations, defaultSizeConversion } from 'helpers'
import { SHIPPING_METHODS } from 'types/shipping'
import Hint from '../Hint'
import SizesGrid from '../SizesGrid'

const ProductSizeOptions = ({
  infoCallout,
  fittingNote,
  allSizeConversions,
  sizeConversionIdsAndPrices,
  selectedSizeConversionId,
  selectedFormattedDisplayPrice,
  onSizeSelect,
  groupType,
  defaultSizeSystem
}: ProductSizeOptionsProps): JSX.Element => {
  const { locale, t } = useTranslation()

  const [selectedConversion, setSelectedConversion] = React.useState(
    defaultSizeConversion(locale)
  )

  const availableSizeConversionIds = sizeConversionIdsAndPrices.map(
    (obj) => obj.size_conversion_id
  )

  const inStockSizeConversions = React.useMemo(
    () =>
      allSizeConversions.filter(
        (s) => availableSizeConversionIds.indexOf(s.id) > -1
      ),
    [allSizeConversions, availableSizeConversionIds]
  )

  const getInfoCalloutText = () => {
    if (infoCallout && Object.keys(infoCallout).length > 0) {
      if (infoCallout[String(selectedConversion)]) {
        return infoCallout[String(selectedConversion)]
      }

      if (infoCallout.default) {
        return infoCallout.default
      }
    }

    return ''
  }

  const sizeAvailable = availableSizeConversionIds.includes(
    selectedSizeConversionId
  )

  const infoCalloutText = getInfoCalloutText()

  const sizeConversions: SizeConversion[] = inStockSizeConversions.map(
    (sizeConversion) => {
      const premiumShipAvailable = sizeConversionIdsAndPrices.find(
        (obj) =>
          obj.size_conversion_id === sizeConversion.id &&
          obj.shipping_method === SHIPPING_METHODS.PREMIUM
      )

      return {
        ...sizeConversion,
        active: selectedSizeConversionId === sizeConversion.id,
        premiumShipAvailable: Boolean(premiumShipAvailable)
      }
    }
  )

  // temporarily removed to see how conversion is affected
  /* const displayTCPDisclaimer = React.useMemo(
    () =>
      sizeConversionIdsAndPrices.find(
        (obj) => obj.size_conversion_id === selectedSizeConversionId
      )?.contains_customs_charge,

    [sizeConversionIdsAndPrices, selectedSizeConversionId]
  ) */

  return (
    <div className="product-options">
      <div>
        {fittingNote && <p className="footnote fitting-note">{fittingNote}</p>}
        {inStockSizeConversions &&
          inStockSizeConversions.length > 0 &&
          infoCalloutText.length > 0 && (
            <Hint text={infoCalloutText} type="info" />
          )}
        {inStockSizeConversions && inStockSizeConversions.length > 0 ? (
          <SizesGrid
            title={t(`react_components.product.${groupType}.available`)}
            onConversionChange={setSelectedConversion}
            onSizeConversionSelected={onSizeSelect}
            sizeConversions={sizeConversions}
            defaultSizeSystem={defaultSizeSystem}
          />
        ) : (
          <div className="product-options__units">
            <span className="product-options__title">
              {t(`react_components.product.${groupType}.available`)}:{' '}
              <span className="product-options__title--highlight">
                {t(`react_components.product.${groupType}.not_available`)}
              </span>
            </span>
          </div>
        )}
      </div>
      {inStockSizeConversions && inStockSizeConversions.length > 0 && (
        <div className="product-options__item">
          <div className="product-options__detail">
            {sizeAvailable ? (
              <>
                <Typography.Heading as="h1">
                  {selectedFormattedDisplayPrice ||
                    t('react_components.product.sold_out')}
                </Typography.Heading>
              </>
            ) : (
              <span>
                {t(`react_components.product.${groupType}.not_in_stock`)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default withTranslations(ProductSizeOptions)

type ProductSizeOptionsProps = {
  infoCallout?: { [key: string]: string }
  fittingNote: string | null
  allSizeConversions: SizeConversion[]
  sizeConversionIdsAndPrices: SizeConversionIdsAndPrices[]
  selectedSizeConversionId: number
  selectedPrice?: string
  onSizeSelect: (sizeConversion: SizeConversion) => void
  selectedFormattedDisplayPrice: string
  groupType: string
  defaultSizeSystem?: Conversion
}
